/**
 * v2.0.1 TransactionEvent — single action covering Started, Updated, Ended.
 *
 * v1.6 split the lifecycle into StartTransaction (CP→CSMS at session
 * start) and StopTransaction (CP→CSMS at session end) with per-tx
 * MeterValues messages in between. v2.0.1 unifies them as a single
 * `TransactionEvent` with `eventType: Started | Updated | Ended` and
 * embeds `meterValue: MeterValueType[]` directly in Updated frames.
 *
 * Wire vs domain TransactionId
 *   v2.0.1 carries `transactionInfo.transactionId` as a `string`
 *   (maxLength 36). The domain `TransactionId` is a numeric brand
 *   (int32 range — see `domain/types/brands.ts`). The simulator owns
 *   the txid (it picks it when emitting Started), so we just
 *   `.toString()` at the wire boundary and `Number()` back at
 *   inbound. The 2.0.1 string format never leaves the adapter layer
 *   — matches CLAUDE.md §4.3 (the same principle as ConnectorId
 *   tuple translation).
 */
import { StopReason } from '@/domain/types';
import type {
  AuthorizeResponse,
  StartTransactionDomain,
  StopTransactionDomain,
} from '@/protocol/OcppAdapter';
import {
  DEFAULT_ID_TOKEN_TYPE_V201,
  toDomainAuthStatus,
  type AuthorizationStatusV201,
  type IdTokenTypeV201,
} from './authorize';
import { toV201ConnectorStatus } from './statusNotification';

export type TransactionEventType = 'Started' | 'Updated' | 'Ended';

export type TriggerReasonV201 =
  | 'Authorized'
  | 'CablePluggedIn'
  | 'ChargingRateChanged'
  | 'ChargingStateChanged'
  | 'Deauthorized'
  | 'EnergyLimitReached'
  | 'EVCommunicationLost'
  | 'EVConnectTimeout'
  | 'MeterValueClock'
  | 'MeterValuePeriodic'
  | 'TimeLimitReached'
  | 'Trigger'
  | 'UnlockCommand'
  | 'StopAuthorized'
  | 'EVDeparted'
  | 'EVDetected'
  | 'RemoteStop'
  | 'RemoteStart'
  | 'AbnormalCondition'
  | 'SignedDataReceived'
  | 'ResetCommand';

export type ChargingStateV201 = 'Charging' | 'EVConnected' | 'SuspendedEV' | 'SuspendedEVSE' | 'Idle';

export type ReasonV201 =
  | 'DeAuthorized'
  | 'EmergencyStop'
  | 'EnergyLimitReached'
  | 'EVDisconnected'
  | 'GroundFault'
  | 'ImmediateReset'
  | 'Local'
  | 'LocalOutOfCredit'
  | 'MasterPass'
  | 'Other'
  | 'OvercurrentFault'
  | 'PowerLoss'
  | 'PowerQuality'
  | 'Reboot'
  | 'Remote'
  | 'SOCLimitReached'
  | 'StoppedByEV'
  | 'TimeLimitReached'
  | 'Timeout'
  // 2.1-only — legal on the 2.1 schema, rejected by the 2.0.1 schema. The
  // version-aware reason picker only offers it on a 2.1 station, so it can
  // only ever reach a 2.1 wire; the vendored Ajv schema is the backstop.
  | 'ReqEnergyTransferRejected';

/**
 * Domain StopReason → v2.0.1 / 2.1 ReasonEnumType. The 2.x-native values map
 * 1:1. The three 1.6-only values (HardReset / SoftReset / UnlockCommand) have
 * no 2.x equivalent and fold to the closest match. Total over StopReason so
 * adding a domain value forces a decision here.
 */
export function toV201StopReason(reason: StopReason): ReasonV201 {
  switch (reason) {
    // 1.6-only — fold to closest 2.x value
    case StopReason.HardReset:
    case StopReason.SoftReset:
      return 'ImmediateReset';
    case StopReason.UnlockCommand:
      return 'Local'; // v2.x has no UnlockCommand
    // shared + 2.x-native — identity
    case StopReason.DeAuthorized:
      return 'DeAuthorized';
    case StopReason.EmergencyStop:
      return 'EmergencyStop';
    case StopReason.EnergyLimitReached:
      return 'EnergyLimitReached';
    case StopReason.EVDisconnected:
      return 'EVDisconnected';
    case StopReason.GroundFault:
      return 'GroundFault';
    case StopReason.ImmediateReset:
      return 'ImmediateReset';
    case StopReason.Local:
      return 'Local';
    case StopReason.LocalOutOfCredit:
      return 'LocalOutOfCredit';
    case StopReason.MasterPass:
      return 'MasterPass';
    case StopReason.Other:
      return 'Other';
    case StopReason.OvercurrentFault:
      return 'OvercurrentFault';
    case StopReason.PowerLoss:
      return 'PowerLoss';
    case StopReason.PowerQuality:
      return 'PowerQuality';
    case StopReason.Reboot:
      return 'Reboot';
    case StopReason.Remote:
      return 'Remote';
    case StopReason.SOCLimitReached:
      return 'SOCLimitReached';
    case StopReason.StoppedByEV:
      return 'StoppedByEV';
    case StopReason.TimeLimitReached:
      return 'TimeLimitReached';
    case StopReason.Timeout:
      return 'Timeout';
    case StopReason.ReqEnergyTransferRejected:
      return 'ReqEnergyTransferRejected';
  }
}

const DEFAULT_EVSE_ID = 1;

export interface BuildStartedOptions {
  readonly transactionId: number;
  readonly req: StartTransactionDomain;
  readonly idTokenType?: IdTokenTypeV201;
}

export function buildTransactionEventStarted(
  opts: BuildStartedOptions,
): Record<string, unknown> {
  const idTokenType = opts.idTokenType ?? DEFAULT_ID_TOKEN_TYPE_V201;
  return {
    eventType: 'Started',
    timestamp: opts.req.timestamp,
    triggerReason: 'Authorized' satisfies TriggerReasonV201,
    seqNo: 0,
    transactionInfo: {
      transactionId: String(opts.transactionId),
      chargingState: 'Charging' satisfies ChargingStateV201,
    },
    evse: { id: DEFAULT_EVSE_ID, connectorId: opts.req.connectorId },
    idToken: { idToken: opts.req.idTag, type: idTokenType },
    meterValue: [
      {
        timestamp: opts.req.timestamp,
        sampledValue: [
          {
            value: opts.req.meterStart,
            context: 'Transaction.Begin',
            measurand: 'Energy.Active.Import.Register',
          },
        ],
      },
    ],
  };
}

export interface BuildUpdatedOptions {
  readonly transactionId: number;
  readonly seqNo: number;
  readonly timestamp: string;
  readonly connectorId: number;
  readonly meterValue: readonly {
    readonly timestamp: string;
    readonly sampledValue: readonly Record<string, unknown>[];
  }[];
}

export function buildTransactionEventUpdated(
  opts: BuildUpdatedOptions,
): Record<string, unknown> {
  return {
    eventType: 'Updated',
    timestamp: opts.timestamp,
    triggerReason: 'MeterValuePeriodic' satisfies TriggerReasonV201,
    seqNo: opts.seqNo,
    transactionInfo: {
      transactionId: String(opts.transactionId),
      chargingState: 'Charging' satisfies ChargingStateV201,
    },
    evse: { id: DEFAULT_EVSE_ID, connectorId: opts.connectorId },
    meterValue: opts.meterValue.map((mv) => ({
      timestamp: mv.timestamp,
      sampledValue: mv.sampledValue,
    })),
  };
}

export interface BuildEndedOptions {
  readonly transactionId: number;
  readonly seqNo: number;
  readonly req: StopTransactionDomain;
  readonly connectorId: number;
  readonly idTag?: string;
  readonly idTokenType?: IdTokenTypeV201;
}

export function buildTransactionEventEnded(
  opts: BuildEndedOptions,
): Record<string, unknown> {
  const reason: ReasonV201 = opts.req.reason ? toV201StopReason(opts.req.reason) : 'Local';
  const triggerReason: TriggerReasonV201 = reason === 'Remote' ? 'RemoteStop' : 'StopAuthorized';
  const wire: Record<string, unknown> = {
    eventType: 'Ended',
    timestamp: opts.req.timestamp,
    triggerReason,
    seqNo: opts.seqNo,
    transactionInfo: {
      transactionId: String(opts.transactionId),
      chargingState: 'Idle' satisfies ChargingStateV201,
      stoppedReason: reason,
    },
    evse: { id: DEFAULT_EVSE_ID, connectorId: opts.connectorId },
    meterValue: [
      {
        timestamp: opts.req.timestamp,
        sampledValue: [
          {
            value: opts.req.meterStop,
            context: 'Transaction.End',
            measurand: 'Energy.Active.Import.Register',
          },
        ],
      },
    ],
  };
  // Optionally surface the id token at Ended (e.g. for off-board metering).
  if (opts.idTag !== undefined) {
    wire['idToken'] = { idToken: opts.idTag, type: opts.idTokenType ?? DEFAULT_ID_TOKEN_TYPE_V201 };
  }
  // Touch toV201ConnectorStatus so the import stays referenced for future
  // chargingState ↔ status mapping in 10d (E2E + UI integration).
  void toV201ConnectorStatus;
  return wire;
}

/**
 * Parse TransactionEventResponse. v2.0.1 response has no transactionId
 * (the CP minted it). Map to the domain `StartTransactionResponse` /
 * `StopTransactionResponse` shape at the call site by passing in the
 * txid the CP minted.
 */
export function parseTransactionEventIdTokenInfo(
  payload: Record<string, unknown>,
): AuthorizeResponse['idTagInfo'] | undefined {
  const info = payload['idTokenInfo'] as Record<string, unknown> | undefined;
  if (!info) return undefined;
  const status = toDomainAuthStatus(info['status'] as AuthorizationStatusV201);
  return {
    status,
    ...(typeof info['cacheExpiryDateTime'] === 'string'
      ? { expiryDate: info['cacheExpiryDateTime'] }
      : {}),
    ...(typeof info['groupIdToken'] === 'object' && info['groupIdToken'] !== null
      ? {
          parentIdTag: (info['groupIdToken'] as Record<string, unknown>)['idToken'] as string,
        }
      : {}),
  };
}
